<script lang="ts">
  // Group · Peloton: who you rode with, the group's pulling power, and the route.
  import type { ReportContext } from '../context'
  import { statsFor } from '../stats'
  import Avatar from '../parts/Avatar.svelte'
  import Badges from '../parts/Badges.svelte'
  import Brand from '../parts/Brand.svelte'
  import Fill from '../parts/Fill.svelte'
  import Frame from '../parts/Frame.svelte'
  import Icon from '../parts/Icon.svelte'
  import PowerChart from '../parts/PowerChart.svelte'
  import RouteMap from '../parts/RouteMap.svelte'
  import Shield from '../parts/Shield.svelte'
  import StatGrid from '../parts/StatGrid.svelte'
  import TitleBlock from '../parts/TitleBlock.svelte'

  export let ctx: ReportContext

  $: MAX_AVATARS = ctx.size === 'landscape' ? 5 : 7

  $: g = ctx.data.group!
  $: ride = g.ride
  $: session = g.session
  $: others = ride ? ride.riders_total : Math.max(0, (session?.participants ?? 1) - 1)
  $: riders = ride ? ride.riders : (session?.leaderboard ?? []).filter((r) => !r.is_me)
  $: shown = riders.slice(0, MAX_AVATARS)
  $: extra = Math.max(0, others - shown.length)
  $: names = riders.filter((r) => !r.blurred && r.name).map((r) => r.name!.split(' ')[0])
  $: namesLine = (() => {
    if (!names.length) return null
    const list = names.slice(0, 3)
    const rest = others - list.length
    return 'with ' + list.join(', ') + (rest > 0 ? ` and ${rest} other${rest === 1 ? '' : 's'}` : '')
  })()
  $: stats = statsFor(ctx, ['stat.distance', 'stat.rating', 'stat.powerChoice', 'stat.xss', 'stat.time', 'stat.speed', 'stat.elevation', 'stat.recovery'], { highest: true })
  $: hasHigh = stats.some((s) => s.highest)
  $: hasRoute = ctx.show('route') && !!ctx.data.route
  // The smallest height (px) the route or chart is still worth drawing at.
  const MIN_GRAPHIC = 90
  $: meSize = { portrait: 128, square: 100, landscape: 120 }[ctx.size]
  $: riderSize = { portrait: 96, square: 76, landscape: 88 }[ctx.size]
</script>

<Frame {ctx} base="radial-gradient(120% 70% at 100% 0%, #1d4442 0%, #0d1d1e 55%, #060b0c 100%)">
  <div class="xr-layout">
    <div class="xr-top">
      <span class="xr-eyebrow"><Icon name="users" size={30} stroke={2.4} color="var(--accent)" /> {ride ? 'Group Ride' : 'Group Session'}</span>
      <Brand height={38} />
    </div>

    <div class="xr-head">
      <h2 class="xr-headline">
        {#if others > 0}Rode with <span class="xr-accent">{others}</span> Xert Rider{others === 1 ? '' : 's'}{:else}Group ride{/if}
      </h2>
      {#if ride?.squad && ctx.show('squad')}
        <div class="xr-squad">{ride.squad}</div>
      {:else if session}
        <div class="xr-squad">{session.name}</div>
      {/if}
    </div>

    {#if ctx.show('riders')}
      <div class="xr-riders">
        <div class="xr-crowd">
          <div class="xr-me"><Avatar src={ctx.data.athlete.avatar} name={ctx.data.athlete.name} size={meSize} ring="var(--accent)" ringWidth={6} /></div>
          <div class="xr-stack">
            {#each shown as r}
              <div class="xr-slot"><Avatar src={r.avatar} name={r.name} blurred={r.blurred} size={riderSize} ring="#0d1d1e" ringWidth={6} /></div>
            {/each}
            {#if extra > 0}
              <div class="xr-slot xr-more" style="width: {riderSize}px; height: {riderSize}px;">+{extra}</div>
            {/if}
          </div>
        </div>
        {#if ctx.show('names') && namesLine}
          <div class="xr-names">{namesLine}</div>
        {/if}
      </div>
    {/if}

    {#if ctx.show('pulling') && ride?.pulling_power}
      <div class="xr-pull">
        <span class="xr-pull-label">Group Pulling Power</span>
        <span class="xr-pull-value">{ride.pulling_power}<small>W</small></span>
      </div>
    {/if}

    <!-- Takes only the space the other fields leave (no minimum), so it never spills over the
         stats when more of them are switched on; left out when there is too little to read. -->
    <div class="xr-graphic">
      {#if hasRoute && ctx.data.route}
        <Fill minHeight={0} let:width let:height>
          {#if height >= MIN_GRAPHIC}
            <RouteMap route={ctx.data.route} trim={ctx.options.trimRoute} {width} {height} strokeWidth={7} padding={20} />
          {/if}
        </Fill>
      {:else if ctx.show('chart') && ctx.data.series?.power}
        <Fill minHeight={0} let:width let:height>
          {#if height >= MIN_GRAPHIC}
            <PowerChart series={ctx.data.series} colored={ctx.options.colorPowerChart} showHr={ctx.options.chartHeartRate} {width} {height} showMpa={false} breakthrough={ctx.data.breakthrough?.level ?? null} />
          {/if}
        </Fill>
      {/if}
    </div>

    <div class="xr-stats">
      <StatGrid {stats} columns={4} size="sm" />
      {#if hasHigh}
        <div class="xr-footnote"><Shield size={24} /> = Highest of the group</div>
      {/if}
    </div>
    <div class="xr-title"><TitleBlock {ctx} lines={1} /></div>
    <div class="xr-badges"><Badges {ctx} own="group" max={ctx.size === 'square' ? 2 : Infinity} /></div>
  </div>
</Frame>

<style>
  .xr-layout {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto auto auto minmax(0, 1fr) auto auto auto;
    grid-template-areas: 'top' 'head' 'riders' 'pull' 'graphic' 'stats' 'title' 'badges';
    row-gap: 28px;
  }
  :global(.xr-square) .xr-layout { row-gap: 20px; }
  /* Landscape: who you rode with on the left, the route across the right. */
  :global(.xr-landscape) .xr-layout {
    grid-template-columns: 640px minmax(0, 1fr);
    grid-template-rows: auto auto auto auto minmax(0, 1fr) auto auto;
    grid-template-areas:
      'top top'
      'head graphic'
      'riders graphic'
      'pull graphic'
      '. graphic'
      'title stats'
      'badges badges';
    column-gap: 70px;
    row-gap: 28px;
  }
  .xr-top { grid-area: top; display: flex; align-items: center; justify-content: space-between; }
  .xr-head { grid-area: head; min-width: 0; }
  .xr-riders { grid-area: riders; }
  .xr-pull { grid-area: pull; }
  .xr-graphic { grid-area: graphic; display: flex; flex-direction: column; min-height: 0; }
  .xr-stats { grid-area: stats; }
  .xr-title { grid-area: title; min-width: 0; }
  :global(.xr-landscape) .xr-title, :global(.xr-landscape) .xr-stats { align-self: end; }
  .xr-badges { grid-area: badges; }
  .xr-badges:empty { display: none; }
  :global(.xr-square) .xr-headline { font-size: 92px; }
  :global(.xr-landscape) .xr-headline { font-size: 104px; }
  :global(.xr-square) .xr-more { font-size: 34px; }
  :global(.xr-square) .xr-names { margin-top: 14px; font-size: 26px; }
  :global(.xr-square) .xr-pull { padding: 12px 24px; }
  :global(.xr-square) .xr-pull-value { font-size: 60px; }
  .xr-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-size: 26px;
    font-weight: 800;
    letter-spacing: 0.26em;
    text-transform: uppercase;
  }
  .xr-headline {
    font-family: var(--display);
    font-weight: 800;
    font-size: 116px;
    line-height: 0.92;
    text-transform: uppercase;
  }
  .xr-accent {
    color: var(--accent);
  }
  .xr-squad {
    margin-top: 12px;
    font-size: 32px;
    font-weight: 600;
    color: var(--muted);
  }
  .xr-crowd {
    display: flex;
    align-items: center;
    gap: 34px;
  }
  .xr-stack {
    display: flex;
    align-items: center;
  }
  .xr-slot {
    margin-left: -14px;
  }
  .xr-slot:first-child {
    margin-left: 0;
  }
  .xr-more {
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--accent);
    color: #0b1114;
    font-family: var(--display);
    font-size: 42px;
    font-weight: 800;
    box-shadow: 0 0 0 6px #0d1d1e;
  }
  .xr-names {
    margin-top: 20px;
    font-size: 30px;
    color: var(--muted);
  }
  .xr-footnote {
    margin-top: 18px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 22px;
    color: var(--muted);
  }
  .xr-pull {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 26px;
    border-radius: 20px;
    background: var(--panel);
    border: 2px solid var(--faint);
  }
  .xr-pull-label {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .xr-pull-value {
    font-family: var(--display);
    font-size: 72px;
    font-weight: 800;
    line-height: 1;
    color: var(--accent);
  }
  .xr-pull-value small {
    margin-left: 6px;
    font-size: 0.45em;
    color: var(--muted);
  }
</style>
